import { useMemo } from 'react';
import type { Device, Trigger } from '@/hooks/useTriggerStream';
import { hashTo } from './vizPalette';
import { useT } from '@/i18n/LanguageContext';
import { visualizeContent } from '@/i18n/content/visualize';

interface Props {
  triggers: Trigger[];
  devices: Device[];
}

// Two triggers on different sculptures within this window count as one
// "interaction" travelling between them through the network.
const LINK_WINDOW_MS = 30_000;
const W = 800;
const H = 800;

function rng(seed: number) {
  let s = seed || 1;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

/** Builds weighted links between sculptures from consecutive triggers. */
export function buildLinks(triggers: Trigger[]) {
  const sorted = [...triggers].sort((a, b) => a.created_at.localeCompare(b.created_at));
  const links = new Map<string, number>();
  for (let i = 1; i < sorted.length; i++) {
    const a = sorted[i - 1];
    const b = sorted[i];
    if (a.device_id === b.device_id) continue;
    if (new Date(b.created_at).getTime() - new Date(a.created_at).getTime() > LINK_WINDOW_MS) continue;
    const key = [a.device_id, b.device_id].sort().join('|');
    links.set(key, (links.get(key) ?? 0) + 1);
  }
  return links;
}

export function MyceliumNetwork({ triggers, devices }: Props) {
  const t = useT(visualizeContent).charts;
  const { nodes, paths, hairs, totalInteractions } = useMemo(() => {
    const counts = new Map<string, number>();
    triggers.forEach((tr) => counts.set(tr.device_id, (counts.get(tr.device_id) ?? 0) + 1));
    const ids = devices.map((d) => d.device_id).filter((id) => counts.has(id));
    const cx = W / 2;
    const cy = H / 2;
    const pos = new Map<string, { x: number; y: number }>();
    ids.forEach((id, i) => {
      const a = (i / Math.max(1, ids.length)) * Math.PI * 2 - Math.PI / 2;
      const r = 260 + ((hashTo(id) % 60) - 30);
      pos.set(id, { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
    });
    const maxCount = Math.max(1, ...counts.values());

    const links = buildLinks(triggers);
    const maxLink = Math.max(1, ...links.values());
    const paths: { d: string; w: number; o: number }[] = [];
    let totalInteractions = 0;
    links.forEach((n, key) => {
      const [a, b] = key.split('|');
      const p = pos.get(a);
      const q = pos.get(b);
      if (!p || !q) return;
      totalInteractions += n;
      const strength = Math.log1p(n) / Math.log1p(maxLink);
      const strands = 1 + Math.round(strength * 6);
      const rand = rng(hashTo(key));
      for (let s = 0; s < strands; s++) {
        // Wandering filament: a cubic curve pulled toward the centre with jitter.
        const bend = () => (rand() - 0.5) * 160;
        const c1x = p.x + (cx - p.x) * 0.35 + bend();
        const c1y = p.y + (cy - p.y) * 0.35 + bend();
        const c2x = q.x + (cx - q.x) * 0.35 + bend();
        const c2y = q.y + (cy - q.y) * 0.35 + bend();
        paths.push({
          d: `M${p.x},${p.y} C${c1x},${c1y} ${c2x},${c2y} ${q.x},${q.y}`,
          w: s === 0 ? 0.6 + strength * 3.2 : 0.3 + rand() * strength * 1.2,
          o: 0.25 + strength * 0.6,
        });
      }
    });

    // Fine root hairs radiating from each node, more for busier sculptures.
    const hairs: string[] = [];
    ids.forEach((id) => {
      const p = pos.get(id)!;
      const rand = rng(hashTo(id, 7));
      const n = 8 + Math.round((counts.get(id)! / maxCount) * 30);
      for (let i = 0; i < n; i++) {
        const a = rand() * Math.PI * 2;
        const len = 18 + rand() * 55;
        const mx = p.x + Math.cos(a + (rand() - 0.5)) * len * 0.5;
        const my = p.y + Math.sin(a + (rand() - 0.5)) * len * 0.5;
        hairs.push(`M${p.x},${p.y} Q${mx},${my} ${p.x + Math.cos(a) * len},${p.y + Math.sin(a) * len}`);
      }
    });

    const nodes = ids.map((id) => ({
      id,
      ...pos.get(id)!,
      r: 4 + (counts.get(id)! / maxCount) * 8,
      name: devices.find((d) => d.device_id === id)?.name ?? id.slice(-5),
    }));
    return { nodes, paths, hairs, totalInteractions };
  }, [triggers, devices]);

  return (
    <div className="rounded-sm overflow-hidden" style={{ background: '#000' }}>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={t.myceliumAria}>
        <defs>
          <radialGradient id="myc-glow">
            <stop offset="0%" stopColor="hsl(110 90% 85%)" stopOpacity="1" />
            <stop offset="40%" stopColor="hsl(112 90% 45%)" stopOpacity="0.6" />
            <stop offset="100%" stopColor="hsl(112 90% 30%)" stopOpacity="0" />
          </radialGradient>
        </defs>
        <g fill="none" stroke="hsl(112 70% 45%)" strokeLinecap="round">
          {paths.map((p, i) => (
            <path key={i} d={p.d} strokeWidth={p.w} strokeOpacity={p.o} />
          ))}
        </g>
        <g fill="none" stroke="hsl(110 40% 60%)" strokeWidth={0.4} strokeOpacity={0.35}>
          {hairs.map((d, i) => (
            <path key={i} d={d} />
          ))}
        </g>
        {nodes.map((n) => (
          <g key={n.id}>
            <circle cx={n.x} cy={n.y} r={n.r * 3} fill="url(#myc-glow)" />
            <circle cx={n.x} cy={n.y} r={n.r * 0.5} fill="hsl(110 90% 92%)" />
            <text x={n.x} y={n.y + n.r * 3 + 12} textAnchor="middle" fontSize="10" fontFamily="monospace" fill="hsl(110 20% 70%)" fillOpacity={0.7}>
              {n.name}
            </text>
          </g>
        ))}
        <text x={24} y={H - 24} fontSize="11" fontFamily="monospace" fill="hsl(110 20% 70%)" fillOpacity={0.6}>
          {t.myceliumCaption(nodes.length, totalInteractions)}
        </text>
      </svg>
    </div>
  );
}
